import { cva, type VariantProps } from "class-variance-authority";
import { motion, type HTMLMotionProps } from "motion/react";
import { spring } from "../../lib/motion";
import { cn } from "../../lib/utils";

export const buttonVariants = cva(
  "relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium outline-none transition-colors duration-150 focus-visible:ring-4 focus-visible:ring-ring/30 disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground shadow-mxs hover:bg-primary/88",
        outline: "border border-border bg-background shadow-mxs hover:bg-muted",
        secondary: "bg-secondary text-secondary-foreground hover:bg-[#ececec]",
        ghost: "hover:bg-muted",
        destructive: "text-destructive hover:bg-rework-soft",
        glass: "bg-white/15 text-white backdrop-blur-md hover:bg-white/25",
      },
      size: {
        default: "h-10 px-[18px]",
        sm: "h-8 px-3 text-[13px]",
        md: "h-9 px-3.5 text-[13px]",
        lg: "h-11 px-[22px]",
        icon: "size-9",
        iconSm: "size-8",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

type ButtonProps = HTMLMotionProps<"button"> & VariantProps<typeof buttonVariants>;

export function Button({ className, variant, size, type = "button", ...props }: ButtonProps) {
  return (
    <motion.button
      type={type}
      whileTap={{ scale: 0.97 }}
      transition={spring.snappy}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
